import { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { useCompany } from '@/company/context';
import { CompanyRequestError } from '@/company/api';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import { useAppCatalogLocale } from '@/i18n/context';
import copyCatalog, { type ConnectionAccessCopyKey } from '@/i18n/ns/connection-access';
import commonCatalog from '@/i18n/ns/connect-access';
import { formatCount } from '@/i18n/format';
import {
  stopOwnConnectionWrites,
  updateConnectionPolicy,
  type ConnectionReadItem,
  type ConnectionAccessChoice,
} from '../connection-read-access/api';
import { ConnectionPolicyFields, useConnectionPeople } from './policy-fields';
import { useConnectionContextKey, useCurrentConnectionRequest } from './use-current-request';

export const connectionChoice = (item: ConnectionReadItem): ConnectionAccessChoice => ({
  audience: item.policy?.audience ?? item.audience,
  people: item.policy?.people ?? [],
  changes: item.supportsWrites === false ? 'ask' : (item.policy?.changes ?? 'ask'),
});

export function connectionWho(
  item: ConnectionReadItem,
  companyName: string,
  copy: Readonly<Record<ConnectionAccessCopyKey, string>>,
  locale: string,
) {
  if (item.audience === 'company') return formatCopy(copy.whoEveryone, { company: companyName });
  if (item.audience === 'people')
    return formatCount(
      copy.peopleNoun,
      (item.policy?.people.length ?? 0) + (item.policy?.ownerUserId ? 1 : 0),
      locale,
    );
  return copy.whoOnlyYou;
}

export type ConnectionToolLabel = { title: string; effect: 'read' | 'write' };
export type AccessAction = 'on' | 'off' | 'remove';
/** How the form finished: what the person saved, or that they left it unchanged. */
export type AccessOutcome = 'saved' | 'cancelled' | 'removed';

type FormProps = {
  item: ConnectionReadItem;
  companyName: string;
  tools?: ConnectionToolLabel[];
  onDone: (outcome: AccessOutcome) => void;
  onSaved: (next: ConnectionReadItem) => void;
  onStale: () => void;
  onRemove?: (fetchImpl?: typeof fetch) => Promise<void>;
  removeMessage?: string;
  initialAction?: AccessAction | null;
  /** Whether the reach list (tools or tables) is shown here; the panel shows it on Overview. */
  showReach?: boolean;
  onBusyChange?: (busy: boolean) => void;
};

/**
 * Who can use a connection and what Poneva can do with it, with Turn off and Remove. One form for
 * the connection panel's Access tab and the access sheet: the same request ownership, conflict
 * handling and confirmations in both. Renders its body and footer as siblings for the host's frame.
 */
export function ConnectionAccessForm(props: FormProps) {
  const key = useConnectionContextKey(props.item);
  // A conflict re-reads the connection, and its new revision mounts a fresh form: that form still
  // says why the person's draft gave way, until they act on it.
  const [conflict, setConflict] = useState<{ from: string; to?: string } | null>(null);
  if (conflict && !conflict.to && conflict.from !== key) setConflict({ ...conflict, to: key });
  return (
    <CurrentConnectionAccessForm
      key={key}
      {...props}
      conflicted={conflict?.to === key}
      onConflict={(active) => setConflict(active ? { from: key } : null)}
    />
  );
}

function CurrentConnectionAccessForm({
  conflicted,
  onConflict,
  item,
  companyName,
  tools,
  onDone,
  onSaved,
  onStale,
  onRemove,
  initialAction = null,
  removeMessage,
  showReach = true,
  onBusyChange,
}: FormProps & { conflicted: boolean; onConflict: (active: boolean) => void }) {
  const request = useCurrentConnectionRequest();
  const copy = useNamespaceCopy(copyCatalog);
  const common = useNamespaceCopy(commonCatalog);
  const language = useAppCatalogLocale() ?? 'en';
  const company = useCompany().data;
  const people = useConnectionPeople();
  const [draft, setDraft] = useState(() => connectionChoice(item));
  const [busy, setBusyState] = useState(false);
  const [problem, setProblem] = useState<string | null>(conflicted ? copy.conflict : null);
  const [confirm, setConfirm] = useState<AccessAction | 'own' | null>(initialAction);
  const initial = connectionChoice(item);
  const changed = JSON.stringify(initial) !== JSON.stringify(draft) || !item.access.enabled;
  const name = item.name;
  const owner = people.find((person) => person.userId === item.policy?.ownerUserId);
  const canCompany = ['owner', 'admin'].includes(company?.membership?.role ?? '');
  const setBusy = (value: boolean) => {
    setBusyState(value);
    onBusyChange?.(value);
  };
  // A newer revision can replace this form mid-save (a poll brought it first): its host is never
  // left waiting for an answer this form will no longer report.
  const busyChange = useRef(onBusyChange);
  busyChange.current = onBusyChange;
  useEffect(() => () => busyChange.current?.(false), []);

  async function save(operation: 'save' | 'on' | 'off' | 'own' | 'remove') {
    const owner = request.begin();
    if (!owner) return;
    setBusy(true);
    setProblem(null);
    onConflict(false);
    try {
      if (operation === 'remove') {
        await onRemove?.(owner.fetch);
        if (owner.current()) onStale();
      } else {
        const saved =
          operation === 'own'
            ? await stopOwnConnectionWrites(item, owner.fetch)
            : await updateConnectionPolicy(
                item,
                operation === 'off' ? initial : draft,
                operation !== 'off',
                owner.fetch,
              );
        if (owner.current()) onSaved(saved);
      }
      if (owner.current()) onDone(operation === 'remove' ? 'removed' : 'saved');
    } catch (error) {
      if (!owner.current()) return;
      if (error instanceof CompanyRequestError && error.status === 409) {
        setProblem(copy.conflict);
        onConflict(true);
        onStale();
      } else setProblem(copy.saveFailed);
    } finally {
      if (owner.finish()) setBusy(false);
    }
  }

  const confirmation =
    confirm === 'on'
      ? copy.turnOnConfirm
      : confirm === 'off'
        ? copy.turnOffConfirm
        : confirm === 'own'
          ? copy.ownWriteStopConfirm
          : (removeMessage ?? copy.removeConfirm);
  const what = item.policy?.changes === 'allowed' ? copy.whatReadWrite : copy.whatRead;
  return (
    <>
      <div className="ws-access-sheet-body">
        {item.canSet ? (
          <ConnectionPolicyFields
            value={draft}
            writeUnavailableReason={
              item.supportsWrites === false ? formatCopy(copy.onlyRead, { name }) : undefined
            }
            onChange={setDraft}
            companyName={companyName}
            canCompany={canCompany}
            ownerUserId={item.policy?.ownerUserId ?? null}
            people={people}
            disabled={busy}
          />
        ) : (
          <>
            <h3>{copy.qWho}</h3>
            <p>{connectionWho(item, companyName, copy, language)}</p>
            <h3>{copy.qWhat}</h3>
            <p>{item.policy?.ownChanges.allowed ? formatCopy(copy.forEveryone, { what }) : what}</p>
            <p className="ws-access-note">
              {formatCopy(copy.readOnlyNote, {
                owner: owner?.displayName || owner?.email || copy.ownerTag,
              })}
            </p>
          </>
        )}
        <OwnConnectionAccess item={item} language={language} onStop={() => setConfirm('own')} />
        {showReach && (item.provider === 'postgres' || Boolean(tools?.length)) && (
          <details className="ws-access-reach">
            <summary>{copy.reach}</summary>
            <ConnectionReach item={item} tools={tools} />
          </details>
        )}
        {confirm && (
          <div role="alert">
            <p>{formatCopy(confirmation, { name })}</p>
            <Button
              disabled={busy}
              variant={confirm === 'on' ? 'default' : 'destructive'}
              onClick={() => void save(confirm)}
            >
              {confirm === 'on'
                ? copy.turnOn
                : confirm === 'own'
                  ? copy.ownWriteStopYes
                  : confirm === 'off'
                    ? copy.turnOff
                    : copy.remove}
            </Button>
            <Button disabled={busy} variant="ghost" onClick={() => setConfirm(null)}>
              {copy.ownWriteKeep}
            </Button>
          </div>
        )}
        {problem && <p role="alert">{problem}</p>}
      </div>
      <footer>
        <div>
          {/* While a confirmation is open it is the one way to act; no second Remove beside it. */}
          {item.canSet && !confirm && (
            <>
              {item.access.enabled && (
                <Button
                  variant="ghost"
                  className="ws-access-danger"
                  disabled={busy}
                  onClick={() => setConfirm('off')}
                >
                  {copy.turnOff}
                </Button>
              )}
              {onRemove && (
                <Button
                  variant="ghost"
                  className="ws-access-danger"
                  disabled={busy}
                  onClick={() => setConfirm('remove')}
                >
                  {copy.remove}
                </Button>
              )}
            </>
          )}
        </div>
        <div>
          <Button variant="outline" disabled={busy} onClick={() => onDone('cancelled')}>
            {common.cancel}
          </Button>
          {item.canSet && (
            <Button
              disabled={busy || !changed || (draft.audience === 'people' && !draft.people.length)}
              onClick={() => void save('save')}
            >
              {busy ? common.saving : !item.access.enabled ? copy.turnOn : common.save}
            </Button>
          )}
        </div>
      </footer>
    </>
  );
}

/** What a connection can reach: its database tables, or its tools with read or change tags. */
export function ConnectionReach({
  item,
  tools,
}: {
  item: ConnectionReadItem;
  tools?: ConnectionToolLabel[];
}) {
  const copy = useNamespaceCopy(copyCatalog);
  const common = useNamespaceCopy(commonCatalog);
  return item.provider === 'postgres' ? (
    <ul>
      {item.resources
        .filter((resource) => item.access.resources.includes(resource.id))
        .map((resource) => (
          <li key={resource.id}>
            <span>{resource.label}</span>
          </li>
        ))}
    </ul>
  ) : (
    <>
      <p className="ws-access-note">{copy.reachUnlabelled}</p>
      <ul>
        {tools?.map((tool, index) => (
          <li key={`${index}:${tool.title}`}>
            <span>{tool.title}</span>
            <small>{tool.effect === 'read' ? common.tagRead : common.tagChange}</small>
          </li>
        ))}
      </ul>
    </>
  );
}

/** The access sheet: the form in a dialog, for connection cards outside the Connections page. */
export function ConnectionAccessSheet({
  item,
  onClose,
  ...props
}: Omit<FormProps, 'onDone' | 'onBusyChange'> & { onClose: () => void }) {
  const copy = useNamespaceCopy(copyCatalog);
  const [busy, setBusy] = useState(false);
  return (
    <Dialog open onOpenChange={(open) => !open && !busy && onClose()}>
      <DialogContent className="pc-portal ws-access-sheet">
        <header>
          <DialogTitle>{formatCopy(copy.sheetTitle, { name: item.name })}</DialogTitle>
          <DialogDescription className="ws-access-note">{copy.appliesNext}</DialogDescription>
        </header>
        <ConnectionAccessForm {...props} item={item} onDone={onClose} onBusyChange={setBusy} />
      </DialogContent>
    </Dialog>
  );
}

export function OwnConnectionAccess({
  item,
  language,
  onStop,
}: {
  item: ConnectionReadItem;
  language: string;
  onStop: () => void;
}) {
  const copy = useNamespaceCopy(copyCatalog);
  const own = item.policy?.ownChanges;
  if (item.canSet && !own?.allowed) return null;
  const name = item.name;
  const text =
    own?.allowed && own.grantedAt
      ? formatCopy(copy.ownWrite, {
          name,
          date: new Intl.DateTimeFormat(language, { dateStyle: 'medium' }).format(
            new Date(own.grantedAt),
          ),
        })
      : formatCopy(
          item.supportsWrites === false
            ? copy.onlyRead
            : item.policy?.changes === 'allowed'
              ? copy.audienceWrite
              : copy.ownRead,
          { name },
        );
  return (
    <section className="ws-access-reach">
      <h3>{copy.yourAccess}</h3>
      <p>{text}</p>
      {own?.canStop && (
        <Button variant="outline" className="ws-access-danger" onClick={onStop}>
          {copy.ownWriteStop}
        </Button>
      )}
    </section>
  );
}
